import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
  FaChevronUp,
  FaStore,
  FaUtensils,
} from "react-icons/fa";
import {
  FiBookOpen,
  FiBriefcase,
  FiCreditCard,
  FiGrid,
  FiHelpCircle,
  FiLayers,
  FiLogOut,
  FiMapPin,
  FiMenu,
  FiUser,
  FiUserCheck,
  FiUsers,
  FiX,
} from "react-icons/fi";

import profile from "../restaurant/assets/profile.webp";
import MayaBrandMark from "../components/MayaBrandMark";
import { performLogout } from "../utils/sessionManager";
import { canManageEatsEmployees, isEatsAppManager } from "../utils/eatsAppAuth";
import PartnerBusinessSwitcher from "../partner/PartnerBusinessSwitcher";
import { ROUTES, supportUrl } from "../constants/appRoutes";
import { formatUserFullName } from "../restaurant/formatDisplay";
import useResponsiveTableLabels from "../restaurant/useResponsiveTableLabels";
import "../restaurant/css/Header.css";
import "../restaurant/css/ResponsiveDashboard.css";
import "./EatsAdminLayout.css";

const PHONE_QUERY = "(max-width: 1023px)";

export const ADMIN_SIDEBAR_ITEMS = [
  ["home", "Dashboard"],
  ["stores", "Stores"],
  ["restaurants", "Restaurants"],
  ["organizations", "Organizations"],
  ["customers", "Customers"],
  ["users", "Employees"],
  ["subscriptions", "Subscriptions"],
  ["jreats-menu", "Jr Eats Menu"],
  ["menu-master", "Menu Master"],
  ["pickup-hubs", "Pickup Hubs"],
];

const ADMIN_SIDEBAR_ICONS = {
  home: FiGrid,
  users: FiUsers,
  restaurants: FaUtensils,
  organizations: FiBriefcase,
  stores: FaStore,
  customers: FiUserCheck,
  subscriptions: FiCreditCard,
  "jreats-menu": FiBookOpen,
  "menu-master": FiLayers,
  "pickup-hubs": FiMapPin,
};

/** Sidebar tabs that open dedicated routes instead of dashboard query tabs. */
const ADMIN_SIDEBAR_ROUTES = {
  users: ROUTES.adminEmployees,
};

const SIDEBAR_COLLAPSED_KEY = "eatsAdminSidebarCollapsed";

export const ADMIN_HEADER_OFFSET = 74;

function readStoredUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function openSupportWindow() {
  const width = 720;
  const height = 720;
  const left = window.innerWidth / 2 - width / 2;
  const top = window.innerHeight / 2 - height / 2;
  const features = `width=${width},height=${height},top=${top},left=${left},resizable=yes,scrollbars=yes`;
  const newWindow = window.open(
    supportUrl({ type: "business", name: "Maya Eats Business" }),
    "_blank",
    features
  );
  if (!newWindow) {
    alert("Please allow popups for this website");
  }
}

function handleSignOut() {
  performLogout({ redirectPath: ROUTES.partnerLogin });
}

/**
 * @param {boolean} showAccountMenu — false when the account menu lives in the sidebar footer.
 */
export function EatsAdminHeader({ showAccountMenu = true }) {
  const user = useMemo(readStoredUser, []);
  const displayName = formatUserFullName(user, "App Admin");

  return (
    <header className="restaurant-header">
      <div className="jr-eatslogo-section">
        <MayaBrandMark variant="header" />
      </div>
      {showAccountMenu ? (
        <div className="res-header-container1">
          <button type="button" className="btn-sprt" onClick={openSupportWindow}>
            Support
          </button>
          <div className="maya-account-menu">
            <button type="button" className="maya-account-menu__trigger" aria-haspopup="menu">
              <img src={profile} alt="" className="maya-account-menu__avatar" />
              <span className="maya-account-menu__name">{displayName}</span>
            </button>
            <div className="maya-account-menu__panel" role="menu">
              <div className="maya-account-menu__summary">
                <img src={profile} alt="" className="maya-account-menu__summary-avatar" />
                <div className="maya-account-menu__summary-text">
                  <strong>{displayName}</strong>
                  <span>{user?.email || user?.userName || "Maya Eats Business"}</span>
                </div>
              </div>
              <div className="maya-account-menu__divider" />
              <Link to={ROUTES.userProfile} className="maya-account-menu__item" role="menuitem">
                <FiUser aria-hidden="true" />
                <span>Profile</span>
              </Link>
              <PartnerBusinessSwitcher />
              <div className="maya-account-menu__divider" />
              <button
                type="button"
                className="maya-account-menu__item maya-account-menu__item--danger"
                role="menuitem"
                onClick={handleSignOut}
              >
                <FiLogOut aria-hidden="true" />
                <span>Sign out</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}

/**
 * Profile trigger pinned to the sidebar footer; opens an account menu upward.
 * `children` may be a function receiving `close` so items can dismiss the menu.
 */
export function SidebarAccountMenu({
  name,
  email,
  active = false,
  onSignOut = handleSignOut,
  children,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const location = useLocation();
  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={`maya-account-menu eats-admin-account${open ? " is-open" : ""}`}>
      {open ? (
        <div className="maya-account-menu__panel eats-admin-account__panel" role="menu">
          {typeof children === "function" ? children(close) : children}
          <PartnerBusinessSwitcher />
          <div className="maya-account-menu__divider" />
          <button
            type="button"
            className="maya-account-menu__item maya-account-menu__item--danger"
            role="menuitem"
            onClick={onSignOut}
          >
            <FiLogOut aria-hidden="true" />
            <span>Sign out</span>
          </button>
        </div>
      ) : null}

      <button
        type="button"
        className={`eats-admin-account__trigger${open || active ? " is-active" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title={name}
      >
        <img src={profile} alt="" className="eats-admin-account__avatar" />
        <span className="eats-admin-account__details">
          <span className="eats-admin-account__name">{name}</span>
          <span className="eats-admin-account__email">{email}</span>
        </span>
        <FaChevronUp
          className={`eats-admin-account__caret${open ? " is-open" : ""}`}
          aria-hidden="true"
        />
      </button>
    </div>
  );
}

function readCollapsed(storageKey) {
  try {
    return localStorage.getItem(storageKey) === "1";
  } catch {
    return false;
  }
}

/**
 * Blue left-nav shell: logo + title + collapse arrow, icon links, account menu in the footer.
 * @param {{ id: string, label: string, icon: React.ComponentType, to: string }[]} items
 */
export function AppSidebarLayout({
  title,
  items,
  activeId,
  account,
  collapsedStorageKey = SIDEBAR_COLLAPSED_KEY,
  ariaLabel = "Navigation",
  flushMain = false,
  logo,
  children,
}) {
  const [collapsed, setCollapsed] = useState(() => readCollapsed(collapsedStorageKey));
  const [isPhone, setIsPhone] = useState(() => window.matchMedia?.(PHONE_QUERY).matches ?? false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const mainRef = useRef(null);
  const location = useLocation();
  useResponsiveTableLabels(mainRef);

  useEffect(() => {
    const mql = window.matchMedia?.(PHONE_QUERY);
    if (!mql) return undefined;
    const onChange = (e) => setIsPhone(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search, isPhone]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen]);

  const railOnly = collapsed && !isPhone;
  const brand = (
    <div className="eats-admin-sidebar__brand">
      <span className="eats-admin-sidebar__logo">
        {logo ?? <MayaBrandMark variant="sidebar" />}
      </span>
      <span className="eats-admin-sidebar__title" title={title}>
        {title}
      </span>
    </div>
  );

  const toggleCollapsed = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      try {
        localStorage.setItem(collapsedStorageKey, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  }, [collapsedStorageKey]);

  return (
    <div
      className={`eats-admin-shell${railOnly ? " eats-admin-shell--collapsed" : ""}${
        isPhone ? " eats-admin-shell--phone" : ""
      }${drawerOpen ? " eats-admin-shell--drawer-open" : ""}`}
    >
      <div className="eats-admin-body">
        {isPhone ? (
          <header className="eats-admin-topbar">
            {brand}
            <button
              type="button"
              className="eats-admin-topbar__menu-btn"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              aria-expanded={drawerOpen}
            >
              <FiMenu size={22} aria-hidden="true" />
            </button>
          </header>
        ) : null}

        {isPhone && drawerOpen ? (
          <div className="eats-admin-drawer-backdrop" onClick={() => setDrawerOpen(false)} aria-hidden="true" />
        ) : null}

        <aside className="eats-admin-sidebar" aria-label={ariaLabel}>
          <div className="eats-admin-sidebar__head">
            {brand}
            {isPhone ? (
              <button
                type="button"
                className="eats-admin-sidebar__collapse-btn"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                title="Close menu"
              >
                <FiX size={16} />
              </button>
            ) : (
              <button
                type="button"
                className="eats-admin-sidebar__collapse-btn"
                onClick={toggleCollapsed}
                aria-label={railOnly ? "Expand sidebar" : "Collapse sidebar"}
                title={railOnly ? "Expand sidebar" : "Collapse sidebar"}
              >
                {railOnly ? <FaAngleDoubleRight size={14} /> : <FaAngleDoubleLeft size={14} />}
              </button>
            )}
          </div>

          <nav className="eats-admin-sidebar__nav">
            {items.map(({ id, label, icon: Icon = FiGrid, to }) => (
              <Link
                key={id}
                to={to}
                className={`eats-admin-sidebar-btn${activeId === id ? " is-active" : ""}`}
                aria-current={activeId === id ? "page" : undefined}
                title={label}
              >
                <Icon className="eats-admin-sidebar-btn__icon" aria-hidden="true" />
                <span className="eats-admin-sidebar-btn__label">{label}</span>
              </Link>
            ))}
          </nav>

          {account ? <div className="eats-admin-sidebar__footer">{account}</div> : null}
        </aside>

        <main
          ref={mainRef}
          className={`eats-admin-main rt-scope${flushMain ? " eats-admin-main--flush" : ""}`}
        >
          {children}
        </main>
      </div>
    </div>
  );
}

const EatsAdminLayout = ({ activeTab = "home", flushMain = false, children }) => {
  const location = useLocation();
  const user = useMemo(readStoredUser, []);

  const canManageEmployees = canManageEatsEmployees(user);
  const dashboardBase = location.pathname.startsWith("/eats/user") ? ROUTES.user : ROUTES.admin;

  const hrefFor = (id) => {
    if (ADMIN_SIDEBAR_ROUTES[id]) return ADMIN_SIDEBAR_ROUTES[id];
    if (!id || id === "home") return `${dashboardBase}?tab=home`;
    return `${dashboardBase}?tab=${encodeURIComponent(id)}`;
  };

  const items = isEatsAppManager(user)
    ? ADMIN_SIDEBAR_ITEMS.filter(([id]) => (id === "users" ? canManageEmployees : true)).map(
        ([id, label]) => ({ id, label, icon: ADMIN_SIDEBAR_ICONS[id], to: hrefFor(id) })
      )
    : [];

  return (
    <AppSidebarLayout
      title="Local Stores"
      items={items}
      activeId={activeTab}
      ariaLabel="Admin navigation"
      flushMain={flushMain}
      account={
        <SidebarAccountMenu
          name={formatUserFullName(user, "App Admin")}
          email={user?.email || user?.userName || "Maya Eats Business"}
          active={location.pathname === ROUTES.userProfile}
        >
          {(close) => (
            <>
              <Link to={ROUTES.userProfile} className="maya-account-menu__item" role="menuitem">
                <FiUser aria-hidden="true" />
                <span>Profile</span>
              </Link>
              <button
                type="button"
                className="maya-account-menu__item"
                role="menuitem"
                onClick={() => {
                  close();
                  openSupportWindow();
                }}
              >
                <FiHelpCircle aria-hidden="true" />
                <span>Support</span>
              </button>
            </>
          )}
        </SidebarAccountMenu>
      }
    >
      {children}
    </AppSidebarLayout>
  );
};

export default EatsAdminLayout;
